import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ValidateOtp, resendVerificationOtp } from '../../Services/APIs/authentication';
import { setClinicAccessToken, setClinicDetails } from '../../Utils/clinicAuth';
import useResolveDestination from './useResolveDestination';
import useOtpCountdown from './useOtpCountdown';
import useFlowState from './useFlowState';
import AuthLayout from './AuthLayout';
import FlowErrorCard from './FlowErrorCard';

const OTP_EXPIRY_SECONDS = 10 * 60;
// backend allows one code per account per 60s; starts armed because arrival always follows a fresh code
const RESEND_COOLDOWN_SECONDS = 60;

const errorClasses = "text-xs text-cv2-destructive mt-1";

const OtpVerification = () => {
    const { email, clear } = useFlowState('clinicSignupVerify', ['email'], '/signup');

    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');
    const [resendMessage, setResendMessage] = useState('');
    const [isResending, setIsResending] = useState(false);
    const { resolveDestination, statusError, isResolving } = useResolveDestination();
    const { formatted, reset: resetCountdown, expired } = useOtpCountdown(OTP_EXPIRY_SECONDS);
    const { secondsLeft: resendCooldown, reset: resetResendCooldown } = useOtpCountdown(RESEND_COOLDOWN_SECONDS);

    const initialValues = { otp: '' };

    const validationSchema = Yup.object().shape({
        otp: Yup.string()
            .matches(/^\d{6}$/, 'OTP must be 6 digits')
            .required('OTP is required'),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitError('');
        setSubmitSuccess('');
        try {
            // this endpoint validates the email before trimming it, so a stray space is rejected
            const response = await ValidateOtp({ authenticate_email: email?.trim(), otp: values.otp });
            const { accessToken, clinic } = response.data?.data || {};
            if (!accessToken) {
                // success and "already verified" both return 200 — only success carries a token
                setSubmitError(response.data?.message || 'This email is already verified. Please log in.');
                setSubmitting(false);
                return;
            }
            setSubmitSuccess(response.data?.message || 'Email verified successfully.');
            setClinicAccessToken(accessToken);
            setClinicDetails(clinic);
            clear();
            await resolveDestination();
        } catch (error) {
            // the 500 message is developer wording, and security-layer rejections carry { error }
            // instead of { message } — generic copy for both, server message for the 401s
            const status = error?.response?.status;
            const message = status >= 500 ? '' : error?.response?.data?.message || error?.data?.message;
            setSubmitError(message || 'Something went wrong. Please try again.');
        }
        setSubmitting(false);
    };

    const handleResend = async () => {
        setResendMessage('');
        setIsResending(true);
        try {
            const response = await resendVerificationOtp({ authenticate_email: email });
            setResendMessage(response.data?.message || 'Verification code resent.');
            resetCountdown();
            resetResendCooldown();
        } catch (error) {
            // 500 carries developer wording; the 429 and 404 messages are user-ready
            const status = error?.response?.status;
            const message = status >= 500 ? '' : error?.response?.data?.message || error?.data?.message;
            setResendMessage(message || 'Could not resend code. Please try again.');
        }
        setIsResending(false);
    };

    if (statusError) {
        return (
            <FlowErrorCard
                variant="signup"
                message={statusError}
                onRetry={resolveDestination}
                isRetrying={isResolving}
            />
        );
    }

    return (
        <AuthLayout variant="signup">

                <div className="flex-1 flex flex-col justify-center">
                    <div className="mb-[30px]">
                        <p className="cv2-auth-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-3.5">
                            Almost There
                        </p>
                        <h1 className="font-aeonik font-normal text-[40px] sm:text-[48px] leading-none tracking-[-0.9px] text-cv2-foreground normal-case mb-3">
                            Verify Your Email
                        </h1>
                        <p className="cv2-auth-subcopy-strong font-aeonik text-sm normal-case">
                            We've sent a code to <span className="text-cv2-foreground">{email || 'your email'}</span>
                        </p>
                    </div>

                    {submitSuccess && (
                        <div className="text-sm text-green-500 bg-green-500/10 border border-green-500/30 rounded-md px-3 py-2 mb-4">
                            {submitSuccess}
                        </div>
                    )}
                    {submitError && (
                        <div className="cv2-auth-error-banner text-sm border rounded-md px-3 py-2 mb-4">
                            {submitError}
                        </div>
                    )}

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting }) => (
                            <Form className="flex flex-col">
                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="otp" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        Verification Code
                                    </label>
                                    <Field
                                        type="text"
                                        name="otp"
                                        inputMode="numeric"
                                        maxLength={6}
                                        placeholder="000000"
                                        className="cv2-auth-input w-full bg-transparent border-0 outline-none font-aeonik text-[15px] tracking-[0.3em] text-cv2-foreground normal-case"
                                    />
                                    <ErrorMessage name="otp" component="div" className={errorClasses} />
                                    <div className={`font-spaceMono text-[9px] tracking-[1.2px] uppercase mt-2 ${expired ? 'text-cv2-destructive' : 'cv2-auth-hint'}`}>
                                        {expired ? 'Code Expired' : `Expires in ${formatted}`}
                                    </div>
                                </div>

                                <div className="cv2-auth-row border-t" />

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="mt-7 w-full h-12 bg-claraview-secondary hover:brightness-110 disabled:opacity-50 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase"
                                >
                                    Verify
                                </button>

                                <button
                                    type="button"
                                    disabled={isResending || resendCooldown > 0}
                                    onClick={handleResend}
                                    className="cv2-auth-resend mt-4 w-full text-center font-aeonik text-[13px] normal-case underline underline-offset-2 disabled:no-underline disabled:cursor-not-allowed"
                                >
                                    {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                                </button>

                                {resendMessage && (
                                    <div className="cv2-auth-hint text-xs mt-2 text-center normal-case font-aeonik">
                                        {resendMessage}
                                    </div>
                                )}
                            </Form>
                        )}
                    </Formik>
                </div>

                <Link to="/login" className="cv2-auth-footer mt-10 text-center font-aeonik text-[13px] normal-case block">
                    Go to Login
                </Link>
        </AuthLayout>
    );
};

export default OtpVerification;
